import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, shareReplay, switchMap } from 'rxjs/operators';
import { HttpInternalService } from '../../data/service/http.service';
import { GodModeService } from '../../data/service/god-mode.service';
import { OrganizationService } from '../../data/service/organization.service';

/** SD-1244: the permission keys in dbo.Permission. */
export const PermissionKeys = {
  financialStatements: 'reports.financial_statements',
  ledgerView: 'ledger.view',
  bankingView: 'banking.view',
  bankingViewAll: 'banking.view_all',
  /** Hide or show bank accounts on the Transactions screen, for the whole organisation. */
  bankingAccountsVisibility: 'banking.accounts_visibility',
  billsView: 'bills.view',
  documentsView: 'documents.view',
  usersList: 'users.list',
  /** SD-1249: log one's own time (the Timesheet). */
  timeLog: 'time.log',
  /** SD-1249: manage the organisation's time clients and categories. */
  timeSettings: 'time.settings',
  /** SD-1253: change what an agent wakes on and what it is told to do each time. */
  agentsManage: 'agents.manage',
} as const;

export interface PermissionState {
  /**
   * False when the server has no permissions yet, or could not be asked. Nothing is hidden then:
   * the server's [RequirePermission] is the boundary, and a menu that vanishes because of a
   * failed request reads as a broken app.
   */
  known: boolean;
  permissions: string[];
}

/** SD-1244: one permission with its default, the organisation's own mask and the one in force. */
export interface PermissionSetting {
  key: string;
  name: string;
  description: string;
  defaultRoleMask: number;
  organisationRoleMask: number | null;
  effectiveRoleMask: number;
}

export interface PermissionDefinition {
  key: string;
  name: string;
  description: string;
  defaultRoleMask: number;
  sortOrder: number;
}

/** Role mask bits, 1 << (RoleId - 1), as on the server. */
export const RoleBits = [
  { bit: 1, label: 'Super admin' },
  { bit: 2, label: 'Admin' },
  { bit: 4, label: 'Submitter' },
  // SD-1252: role 4, shown as Advisor. It follows Admin for now (see PermissionMatrixComponent).
  { bit: 8, label: 'Advisor' },
];

/**
 * SD-1244: what the current user may do in the current organisation -- the same permissions that
 * decide which public API / MCP operations their connections can call.
 *
 * Re-fetched on organisation switch and on entering or leaving god mode, like EntitlementsService.
 */
@Injectable({ providedIn: 'root' })
export class PermissionsService {

  private refreshSignal$ = new BehaviorSubject<number>(0);

  readonly state$: Observable<PermissionState> = combineLatest([
    this.refreshSignal$,
    this.organizationService.selectedOrganizationId,
    this.godMode.godModeUser$
  ]).pipe(
    switchMap(() => this.fetch()),
    shareReplay({ bufferSize: 1, refCount: false })
  );

  constructor(
    private http: HttpInternalService,
    private organizationService: OrganizationService,
    private godMode: GodModeService
  ) { }

  /** Whether the user holds a permission. True while permissions are unknown (see PermissionState.known). */
  has$(key: string): Observable<boolean> {
    return this.state$.pipe(
      map(state => !state.known || state.permissions.indexOf(key) >= 0),
      distinctUntilChanged()
    );
  }

  refresh(): void {
    this.refreshSignal$.next(this.refreshSignal$.value + 1);
  }

  /** The organisation's roles and access (its admins only). */
  organisationSettings(): Observable<{ permissions: PermissionSetting[] }> {
    return this.http.getRequest<any>('permissions/organisation', this.orgParams());
  }

  /** Set the organisation's own mask for a permission, or null to use the default. */
  setOrganisationMask(key: string, allowedRoleMask: number | null): Observable<{ permissions: PermissionSetting[] }> {
    const org = this.organizationService.selectedOrganization;
    const query = org ? `?organizationId=${encodeURIComponent(org)}` : '';
    return this.http.putRequest<any>(`permissions/organisation${query}`, { key, allowedRoleMask });
  }

  /** Super admins: every permission with its platform default. */
  registry(): Observable<PermissionDefinition[]> {
    return this.http.getRequest<PermissionDefinition[]>('admin/permissions');
  }

  /** Super admins: change a permission's platform default. */
  setDefault(key: string, defaultRoleMask: number): Observable<PermissionDefinition> {
    return this.http.putRequest<PermissionDefinition>(`admin/permissions/${encodeURIComponent(key)}`, { defaultRoleMask });
  }

  private orgParams(): any {
    const org = this.organizationService.selectedOrganization;
    return org ? { organizationId: org } : {};
  }

  private fetch(): Observable<PermissionState> {
    return this.http.getRequest<any>('permissions/me', this.orgParams()).pipe(
      map(res => ({
        known: !!(res && res.known),
        permissions: (res && res.permissions) || []
      } as PermissionState)),
      catchError(() => of({ known: false, permissions: [] } as PermissionState))
    );
  }
}
